import React, { useEffect, useState, memo, useCallback } from 'react'
import {
  Backbone,
  Column,
  Container,
  Row,
  Spacer
} from '@smuchile/pkg-unimarc-components'
import {
  BigScreen,
  getBemId,
  getGlobalStyle,
  SmallScreen
} from '@smuchile/pkg-unimarc-components/helpers'
import { ProductDetails } from './ProductDetails'
import { Image } from './ProductDetails/Image'
import { Name } from './ProductDetails/Name'
import { Price } from './ProductDetails/Price'
import {
  tagCourierName,
  getValuesShelfCart,
  updateProductQuantity,
  useEvents,
  useTrigerEvent
} from '@smuchile/pkg-unimarc-hooks'
import storageNotes from 'shared/utils/storageNotes/storageNotes'
import { ShelfDeleteItem } from './ShelfDeleteItem'
import { ICartItem } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/INewCartUnimarc'
import { QuantityWrapper } from './QuantityWrapper/QuantityWrapper'
import { ISimulationItem } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/ISimulation'
import { GrayLink } from './GrayLink'

interface HandleOnSubmit {
  quantity: number
  item: ICartItem
}

interface ShelfProps {
  bemId: string
  item: ICartItem
  isLoading: boolean
  isLoadingByEvent: boolean
  isMobile: boolean
  productDiscount?: ISimulationItem
  handleOnSubmit?: ({ quantity, item }: HandleOnSubmit) => void
  setLimitOnProductAlert: (value: boolean) => void
}

const Shelf = ({
  bemId,
  item,
  isLoading,
  isLoadingByEvent,
  isMobile,
  productDiscount,
  handleOnSubmit,
  setLimitOnProductAlert
}: ShelfProps): React.ReactElement => {
  const { id } = item?.description || {}
  const [quantity, setQuantity] = useState(item?.price.quantity)
  const [updateFinished, setUpdateFinished] = useState(false)
  const [prevQuantity, setPrevQuantity] = useState(item?.price.quantity)
  const [message, setMessage] = useState('')
  const [inputQuantity, setInputQuanity] = useState(false)
  const { trigger } = useTrigerEvent()
  const { showValue } = getValuesShelfCart({
    item: { description: item?.description, price: item?.price },
    quantity
  })
  const isCartLimit: boolean =
    item?.price?.cartLimit > 0 && quantity >= item?.price?.cartLimit
  const unitPrice = item?.price?.sellingPrice
  const paddingSkeleton = isMobile ? '0' : '8px 0 0 0'
  const site = 'Unimarc'

  const hideBetterOption = true

  const updateProductQuantityTag = () => {
    if (quantity === prevQuantity) return

    updateProductQuantity({
      cartPrev: prevQuantity,
      cartRes: quantity,
      inputQuantity: inputQuantity,
      productId: item?.description?.productId,
      site,
      session_store: tagCourierName,
      listId: 'carrito',
      listName: 'carrito',
      tagInfo: item?.tagInfo
    })
    setPrevQuantity(quantity)
  }

  const sumQuantity = () => {
    setQuantity(quantity + 1)
  }

  const restQuantity = () => {
    if (isMobile && quantity - 1 === 0) {
      handleOnSubmit({ quantity: 0, item })
    } else {
      setQuantity(quantity - 1)
    }
  }

  const changeQuantity = (element) => {
    setInputQuanity(true)
    const elementVal = parseInt(element.target.value)
    setQuantity(elementVal)
  }

  const openNote = () => {
    trigger({
      eventType: 'productNote',
      data: {
        show: true,
        item: {
          brand: item?.description?.brandName,
          id: item?.description?.id,
          imageUrl: item?.description?.image,
          measurement: item?.description?.netContent,
          name: item?.description?.name
        }
      }
    })
  }

  const deleteItem = useCallback(() => {
    if (!isMobile) {
      setQuantity(0)
    } else {
      handleOnSubmit({ quantity: 0, item })
    }
  }, [isMobile])

  const quantityToSet = (quantity: number): number => {
    if (item?.price?.cartLimit > 0 && quantity > item?.price?.cartLimit) {
      return item?.price?.cartLimit
    }
    return quantity
  }

  useEvents({
    eventType: 'loadingOrderForm',
    callBack: ({ detail }) => {
      if (detail?.loading && detail?.items?.length) {
        const countItems = detail?.items.find((element) => {
          return element?.id == id
        })
        if (countItems) {
          updateProductQuantityTag()
          setQuantity(quantityToSet(countItems?.quantity))
          if (
            countItems?.cartLimit > 0 &&
            countItems?.quantity >= countItems?.cartLimit
          ) {
            setTimeout(() => {
              setLimitOnProductAlert(true)
            }, 3000)
          }
        }
      } else if (!detail?.loading) {
        updateProductQuantityTag()
        setInputQuanity(false)
        setQuantity(quantityToSet(quantity))
      }
    }
  })

  useEvents({
    eventType: 'productNote',
    callBack: ({ detail }) => {
      if (!detail?.show && detail?.item?.id === item?.description?.id)
        setMessage(storageNotes.getNoteByProductId(item?.description?.id))
    }
  })

  useEvents({
    eventType: 'updateQuantityDone',
    callBack: ({ detail: { finished } }) => {
      setUpdateFinished(finished)
    }
  })

  useEffect(() => {
    const maxQuantityByCartlimit =
      item?.price?.cartLimit > 0 && quantity > item?.price?.cartLimit
        ? item?.price?.cartLimit
        : quantity
    handleOnSubmit({
      quantity: maxQuantityByCartlimit,
      item
    })
  }, [quantity])

  useEffect(() => {
    setUpdateFinished(false)
    if (!isLoadingByEvent) {
      setQuantity(item?.price?.quantity)
    }
  }, [item?.price?.quantity, updateFinished, isLoadingByEvent])

  useEffect(() => {
    setMessage(storageNotes.getNoteByProductId(item?.description?.id))
  }, [])

  if (quantity == 0) {
    updateProductQuantityTag()
    return (
      <Column padding={paddingSkeleton}>
        <Row>
          <Backbone
            borderRadius={getGlobalStyle('--border-radius-md')}
            height={isMobile ? 108 : 140}
          />
        </Row>
        <Spacer.Horizontal size={16} />
      </Column>
    )
  }

  const colorWhenIsLoading = isLoadingByEvent
    ? getGlobalStyle('--color-primary-disabled')
    : getGlobalStyle('--color-neutral-gray-dark')

  return (
    <>
      <BigScreen>
        <>
          <Container
            id={bemId}
            isWrap
            minWidth='100%'
            padding='12px 16px'
          >
            <Row>
              <ShelfDeleteItem
                isLoading={isLoadingByEvent}
                isMobile={false}
                onDelete={deleteItem}
              />
            </Row>
            <Spacer.Horizontal customSize={3} />
            <Row
              alignItems='center'
              backgroundColor={getGlobalStyle('--color-base-white')}
              customHeight='80px'
              justifyContent='between'
              maxHeight='80px'
              position='relative'
              tagName='article'
            >
              <Row alignItems='center'>
                {/* Product Details & Shelf */}
                <Column
                  maxWidth='370px'
                  minWidth='370px'
                >
                  <ProductDetails
                    img={item?.description?.image}
                    name={item?.description?.name}
                  />
                </Column>
                <Spacer.Vertical customSize={20} />
                {/* Price */}
                <Column
                  alignItems='start'
                  maxWidth='max-content'
                >
                  <Price
                    isLoading={isLoading}
                    price={unitPrice}
                    productDiscount={productDiscount?.price}
                  />
                </Column>
                <Spacer.Vertical customSize={30} />
              </Row>
              {/* Quantity Button */}
              <Column
                alignItems='end'
                justifyContent='center'
                maxHeight='100px'
                maxWidth='max-content'
                minHeight='100px'
              >
                <Row>
                  <Container maxWidth='max-content'>
                    <QuantityWrapper
                      changeQuantity={changeQuantity}
                      isCartLimit={isCartLimit}
                      isLoadingQuantity={isLoadingByEvent}
                      isMobile={isMobile}
                      price={item?.price}
                      quantity={quantity}
                      restQuantity={restQuantity}
                      showValue={showValue}
                      sumQuantity={sumQuantity}
                    />
                  </Container>
                </Row>
              </Column>
            </Row>

            {/* extra options */}
            <Spacer.Horizontal size={8} />
            <Container minHeight='17px'>
              <GrayLink
                colorIcon={colorWhenIsLoading}
                colorText={colorWhenIsLoading}
                customIconSize={18}
                iconName={message ? 'MessageCheckIcon' : 'MessageLine'}
                id={getBemId('shelfComments', 'container')}
                label={message ? 'Editar comentario' : 'Agregar comentario'}
                onClick={openNote}
              />
              <Spacer.Vertical size={8} />
              {!hideBetterOption && (
                <GrayLink
                  colorIcon={colorWhenIsLoading}
                  colorText={colorWhenIsLoading}
                  customIconSize={18}
                  iconName='Cube3dIcon'
                  id={getBemId('shelfBestOption', 'container')}
                  label='Mejor opción'
                />
              )}
            </Container>
          </Container>
          <Spacer.Horizontal size={8} />
        </>
      </BigScreen>

      <SmallScreen>
        <>
          <Row
            alignItems='start'
            backgroundColor={getGlobalStyle('--color-base-white')}
            borderRadius={getGlobalStyle('--border-radius-md')}
            id={bemId}
            isWrap
            minWidth='100%'
            padding='8px'
            position='relative'
            tagName='article'
          >
            <Row
              alignItems='center'
              justifyContent='between'
              minHeight='67px'
              position='relative'
            >
              {/* Image [Mobile] */}
              <Row
                alignItems='center'
                minWidth='240px'
              >
                <Column
                  id={getBemId('shelfImage', 'container')}
                  maxWidth='50px'
                >
                  <Image
                    altPicture={item?.description?.name}
                    srcPicture={item?.description?.image}
                  />
                </Column>
                <Spacer.Vertical customSize={10} />
                <Column maxWidth='184px'>
                  {/* Name [Mobile] */}
                  <Name name={item?.description?.name} />
                  <Spacer.Horizontal size={4} />
                  {/* Price [Mobile] */}
                  <Column>
                    <Price
                      isLoading={isLoading}
                      price={unitPrice}
                      productDiscount={productDiscount?.price}
                    />
                  </Column>
                </Column>
              </Row>
              {/* Quantity Button [Mobile] */}
              <QuantityWrapper
                changeQuantity={changeQuantity}
                isCartLimit={isCartLimit}
                isLoadingQuantity={isLoadingByEvent}
                isMobile={isMobile}
                price={item?.price}
                quantity={quantity}
                restQuantity={restQuantity}
                showValue={showValue}
                sumQuantity={sumQuantity}
              />
            </Row>
            <Spacer.Horizontal size={8} />
            {/* extra options */}
            <Container
              alignItems='center'
              minHeight='17px'
            >
              <GrayLink
                colorIcon={colorWhenIsLoading}
                colorText={colorWhenIsLoading}
                customIconSize={15}
                iconName={message ? 'MessageCheckIcon' : 'MessageLine'}
                id={getBemId('shelfComments', 'container')}
                isMobile={true}
                label={message ? 'Editar comentario' : 'Agregar comentario'}
                onClick={openNote}
              />
              <Spacer.Vertical size={8} />
              {!hideBetterOption && (
                <GrayLink
                  colorIcon={colorWhenIsLoading}
                  colorText={colorWhenIsLoading}
                  customIconSize={15}
                  iconName='Cube3dIcon'
                  id={getBemId('shelfBestOption', 'container')}
                  isMobile={true}
                  label='Mejor opción'
                />
              )}
            </Container>
          </Row>
          <Spacer.Horizontal size={4} />
        </>
      </SmallScreen>
    </>
  )
}

const MemoShelf = memo(Shelf)

export { MemoShelf }
